// Node id prefixes keep dimensionA (source) and dimensionB (target) nodes
// distinct even when they share a member label — otherwise a shared name would
// collapse into a single node and could create a cycle.
export const FROM_PREFIX = 'a::';
export const TO_PREFIX = 'b::';

export interface SankeyLink {
  from: string;
  to: string;
  weight: number;
}

export interface SankeyNode {
  id: string;
  name: string;
  // A source (dimension A) node, what a drill-down filters on
  isSource: boolean;
}

// A sankey's links and nodes from its matrix cells (dimensionA sources x
// dimensionB targets, see to2DMatrixCells): each positive cell a weighted
// link, summing any that collapse to the same pair (two members can share a
// display label). Sankey links must be positive weights, so zero/negative
// cells are dropped. Nodes are derived from the links so ids are unique and
// every node is referenced — duplicate or orphan node ids make a sankey
// throw.
const toSankeyLinks = ({
  xCategories,
  yCategories,
  cells,
}: {
  xCategories: string[];
  yCategories: string[];
  cells: { x: number; y: number; value: number }[];
}): { links: SankeyLink[]; nodes: SankeyNode[] } => {
  const linkByKey = new Map<string, SankeyLink>();
  cells
    .filter((cell) => cell.value > 0)
    .forEach((cell) => {
      const fromLabel = yCategories[cell.y];
      const toLabel = xCategories[cell.x];
      if (!fromLabel || !toLabel) {
        return;
      }
      const from = `${FROM_PREFIX}${fromLabel}`;
      const to = `${TO_PREFIX}${toLabel}`;
      const key = `${from} ${to}`;
      const existing = linkByKey.get(key);
      if (existing) {
        existing.weight += cell.value;
      } else {
        linkByKey.set(key, { from, to, weight: cell.value });
      }
    });

  const links = Array.from(linkByKey.values());
  const nodeById = new Map<string, SankeyNode>();
  links.forEach((link) => {
    if (!nodeById.has(link.from)) {
      nodeById.set(link.from, {
        id: link.from,
        name: link.from.slice(FROM_PREFIX.length),
        isSource: true,
      });
    }
    if (!nodeById.has(link.to)) {
      nodeById.set(link.to, {
        id: link.to,
        name: link.to.slice(TO_PREFIX.length),
        isSource: false,
      });
    }
  });
  return { links, nodes: Array.from(nodeById.values()) };
};

export default toSankeyLinks;
